import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OwnedCardDTO, Page, TradeDTO, TradePartnerLimitsDTO } from '@anidex/shared/types';
import { CARD_PAGE_SIZE, pageCursor } from '../../hooks/useListPage';
import { api, queryString } from '../api';
import { hasListIntent } from '../list-intent';
import { invalidateTrades } from './cache';
import { keys } from './keys';

export function useTrades(box: 'incoming' | 'outgoing' | 'history') {
  return useQuery({
    queryKey: keys.trades(box),
    queryFn: () => api<{ items: TradeDTO[] }>(`/trades${queryString({ box })}`),
  });
}

export function useTrade(id: string | undefined) {
  return useQuery({
    queryKey: keys.trade(id ?? ''),
    queryFn: () => api<TradeDTO>(`/trades/${id}`),
    enabled: Boolean(id),
  });
}

export function useTradePartnerLimits(username: string) {
  return useQuery({
    queryKey: ['trade_limits', username],
    queryFn: () => api<TradePartnerLimitsDTO>(`/trades/partners/${encodeURIComponent(username)}/limits`),
    enabled: Boolean(username),
  });
}

export function usePartnerCards(
  username: string | undefined,
  tradeId?: string | null,
  filters: { search?: string; rarity?: string; nametag?: string; sort?: string; page?: number; limit?: number } = {},
) {
  const page = filters.page ?? 0;
  const limit = filters.limit ?? CARD_PAGE_SIZE;
  const intent = hasListIntent(filters);
  return useQuery({
    queryKey: keys.partnerCards(username ?? '', tradeId ?? undefined, filters),
    queryFn: () =>
      api<Page<OwnedCardDTO>>(
        `/trades/partners/${encodeURIComponent(username!)}/cards${queryString({
          tradeId: tradeId || undefined,
          search: filters.search,
          rarity: filters.rarity,
          nametag: filters.nametag,
          sort: filters.sort,
          limit,
          cursor: pageCursor(page, limit),
        })}`,
      ),
    placeholderData: intent ? keepPreviousData : undefined,
    enabled: Boolean(username) && intent,
  });
}

export function useCreateTrade() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      username: string;
      offerCharacterIds: number[];
      requestCharacterIds: number[];
      offerAnima?: number;
      requestAnima?: number;
      note?: string;
      durationMs?: number;
    }) => api<TradeDTO>('/trades', { body: input }),
    onSuccess: () => invalidateTrades(client),
  });
}

export function useCounterTrade() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      tradeId: string;
      offerCharacterIds: number[];
      requestCharacterIds: number[];
      offerAnima?: number;
      requestAnima?: number;
      note?: string;
      durationMs?: number;
    }) =>
      api<TradeDTO>(`/trades/${input.tradeId}/counter`, {
        body: {
          offerCharacterIds: input.offerCharacterIds,
          requestCharacterIds: input.requestCharacterIds,
          offerAnima: input.offerAnima,
          requestAnima: input.requestAnima,
          note: input.note,
          durationMs: input.durationMs,
        },
      }),
    onSuccess: () => invalidateTrades(client),
  });
}

export function useConfirmTrade() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { tradeId: string; revision: number }) =>
      api<TradeDTO>(`/trades/${input.tradeId}/confirm`, { body: { revision: input.revision } }),
    onSuccess: () => invalidateTrades(client),
  });
}

export function useDeclineTrade() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (tradeId: string) => api<{ ok: boolean }>(`/trades/${tradeId}/decline`, { body: {} }),
    onSuccess: () => invalidateTrades(client),
  });
}

export function useCancelTrade() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (tradeId: string) => api<{ ok: boolean }>(`/trades/${tradeId}/cancel`, { body: {} }),
    onSuccess: () => invalidateTrades(client),
  });
}
